/**
 * client/src/components/ui/Toast.tsx
 *
 * Sistema Toast centralizzato per feedback operazioni (successo/errore/info).
 *
 * USAGE:
 *   import { showToast } from '@/components/ui/Toast';
 *   showToast('Operazione completata!', 'success');
 *   showToast('Errore durante il salvataggio', 'error');
 *
 * Caratteristiche:
 * - Animazione slide-up + fade-in
 * - Auto-dismiss dopo 4 secondi
 * - Stackable (fino a 3 toast visibili)
 * - Accessibile (role="alert", aria-live="polite")
 * - GPU-accelerated (transform + opacity, no layout thrashing)
 */

import React, { useState, useCallback, useEffect, createContext, useContext } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertTriangle, Info, X } from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  createdAt: number;
}

interface ToastContextValue {
  showToast: (message: string, type?: ToastType) => void;
}

// ─── Context ────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue>({
  showToast: () => {},
});

export function useToast(): ToastContextValue {
  return useContext(ToastContext);
}

// ─── Static API (per uso fuori da React, es. apiFetch error handler) ────────

let _globalShowToast: ToastContextValue['showToast'] = () => {};

/**
 * API statica per mostrare un toast da qualsiasi parte del codice,
 * anche fuori dal tree React (es. apiFetch, service worker, utility).
 *
 * Richiede che <ToastProvider> sia montato nel root dell'app.
 */
export function showToast(message: string, type: ToastType = 'info'): void {
  _globalShowToast(message, type);
}

// ─── Icons ──────────────────────────────────────────────────────────────────

const ICONS: Record<ToastType, React.ReactNode> = {
  success: <CheckCircle size={18} className="text-green-400" />,
  error:   <XCircle size={18} className="text-red-400" />,
  warning: <AlertTriangle size={18} className="text-amber-400" />,
  info:    <Info size={18} className="text-blue-400" />,
};

const BORDERS: Record<ToastType, string> = {
  success: 'border-green-500/30',
  error:   'border-red-500/30',
  warning: 'border-amber-500/30',
  info:    'border-blue-500/30',
};

// ─── Provider ───────────────────────────────────────────────────────────────

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const show = useCallback((message: string, type: ToastType = 'info') => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((prev) => {
      // Max 3 toast visibili — rimuovi il più vecchio
      const next = [...prev, { id, message, type, createdAt: Date.now() }];
      return next.length > 3 ? next.slice(-3) : next;
    });
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Registra la API statica globale
  useEffect(() => {
    _globalShowToast = show;
    return () => { _globalShowToast = () => {}; };
  }, [show]);

  return (
    <ToastContext.Provider value={{ showToast: show }}>
      {children}

      {/* Toast container — fixed bottom-center, sopra tutto */}
      <div
        className="fixed bottom-24 md:bottom-8 left-1/2 -translate-x-1/2 z-[9999] flex flex-col-reverse gap-2 w-[calc(100%-2rem)] max-w-sm pointer-events-none"
        aria-live="polite"
        aria-label="Notifiche"
      >
        <AnimatePresence mode="popLayout">
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className={`pointer-events-auto flex items-start gap-2.5 rounded-xl bg-gray-900/95 border ${BORDERS[toast.type]} px-4 py-3 shadow-2xl backdrop-blur-sm`}
              role="alert"
            >
              <span className="shrink-0 mt-0.5">{ICONS[toast.type]}</span>
              <p className="flex-1 text-sm text-white/90 leading-snug">{toast.message}</p>
              <button
                onClick={() => dismiss(toast.id)}
                className="shrink-0 p-0.5 rounded-lg text-white/30 hover:text-white/70 transition-colors"
                aria-label="Chiudi notifica"
              >
                <X size={14} />
              </button>

              {/* Auto-dismiss progress bar */}
              <motion.div
                className={`absolute bottom-0 left-0 h-0.5 rounded-full ${
                  toast.type === 'error' ? 'bg-red-500' :
                  toast.type === 'warning' ? 'bg-amber-500' :
                  toast.type === 'success' ? 'bg-green-500' : 'bg-blue-500'
                }`}
                initial={{ width: '100%' }}
                animate={{ width: '0%' }}
                transition={{ duration: 4, ease: 'linear' }}
                onAnimationComplete={() => dismiss(toast.id)}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
